import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useKeycloak } from "@react-keycloak/web";
import { useEffect, useState } from "react";

import Input from '../../Atoms/Input';
import TextArea from '../../Atoms/TextArea';
import Subtitle from '../../Atoms/Subtitle';

import Accordion from '../../Molecules/Accordion';
import Dropdown from '../../Molecules/Dropdown';
import DatePicker from '../../Molecules/DatePicker';
import CompanyComboBox from '../../Molecules/CompanyComboBox';
import InputTags from '../../Molecules/InputTags';

import { projectTypesPT } from '../../../utils/projectTypes';
import { isEmailWhitelisted } from '../../../utils/whitelist';
import { getRandomExampleProject } from '../../../utils/exampleProjects';

import useProjectStore from '../../../hooks/useProjectStore';

const NewProject = ({ companies, onCompanyCreated }) => {
    const navigate = useNavigate();
    const { keycloak, initialized } = useKeycloak();
    const [isUserWhitelisted, setIsUserWhitelisted] = useState(false);

    const { 
        projectDataStore, 
        updateCompany,
        updateFocalPoint,
        updateFocalPointPosition,
        updateFocalPointEmail,
        updateBusinessDescritive,
        updateProjectTitle,
        updateProjectType,
        updateStartDate,
        updateEndDate,
        updateGoal,
        updateBusinessContext,
        updateTargetPublic,
        updateExpectedResults,
        updateImpactedBusinessIndicators,
        updateDeliverables,
        isFormValid,
        resetProject
    } = useProjectStore();

    useEffect(() => {
        if (initialized && keycloak.authenticated && keycloak.tokenParsed?.email) {
            const userEmail = keycloak.tokenParsed.email;
            setIsUserWhitelisted(isEmailWhitelisted(userEmail));
        }
    }, [initialized, keycloak.authenticated, keycloak.tokenParsed]);

    const handleSaveAndContinue = () => {
        if (isFormValid()) {
            navigate('/summary-project');
        } else {
            Swal.fire({
                position: "top-end",
                title: "Por favor, preencha todos os campos obrigatórios",
                showConfirmButton: false,
                timer: 1800,
                background: '#27272A',
                color: '#fff',
            });
            return false
        }
    }

    const handleCancel = () => {
        Swal.fire({
            position: "top-end",
            title: "Rascunho excluído com sucesso",
            showConfirmButton: false,
            timer: 1800,
            background: '#27272A',
            color: '#fff',
        });

        resetProject();
        navigate('/projects');
    }

    const handleQuickFill = () => {
        const exampleProject = getRandomExampleProject();
        
        const matchingCompany = companies.find(
            company => company.name.toLowerCase().includes(exampleProject.customerData.company.name.toLowerCase())
        );
        
        const selectedCompany = matchingCompany || companies[0];
        
        if (!selectedCompany) {
            Swal.fire({
                position: "top-end",
                title: "Nenhuma empresa disponível para preenchimento",
                showConfirmButton: false,
                timer: 1800,
                background: '#27272A',
                color: '#fff',
            });
            return;
        }

        updateCompany(selectedCompany);

        updateFocalPoint(exampleProject.customerData.focalPoint);
        updateFocalPointPosition(exampleProject.customerData.focalPointPosition);
        updateFocalPointEmail(exampleProject.customerData.focalPointEmail);
        updateBusinessDescritive(exampleProject.customerData.businessDescritive);

        updateProjectTitle(exampleProject.projectData.title);
        updateProjectType(exampleProject.projectData.type);
        updateStartDate(exampleProject.projectData.startDate);
        updateEndDate(exampleProject.projectData.endDate);
        updateGoal(exampleProject.projectData.goal);
        updateBusinessContext(exampleProject.projectData.businessContext);
        updateTargetPublic(exampleProject.projectData.targetPublic);

        updateExpectedResults(exampleProject.impact.expectedResults);
        updateImpactedBusinessIndicators(exampleProject.impact.impactedBusinessIndicators);
        updateDeliverables(exampleProject.impact.deliverables);

        Swal.fire({
            position: "top-end",
            title: "Dados preenchidos com sucesso!",
            showConfirmButton: false,
            timer: 1800,
            background: '#27272A',
            color: '#fff',
        });
    }

    const isFormDisabled = () => {
        const { customerData, projectData } = projectDataStore;

        const isCompanyValid = customerData.company && 
            typeof customerData.company === 'object' && 
            customerData.company.id && 
            customerData.company.name &&
            customerData.company.sector;

        const mandatoryFields = {
            company: isCompanyValid,
            businessDescritive: customerData.businessDescritive,
            title: projectData.title,
            type: projectData.type,
            startDate: projectData.startDate,
            goal: projectData.goal,
            targetPublic: projectData.targetPublic
        };

        return Object.values(mandatoryFields).some(field => 
            field === null || field === '' || field.trim() === '' || field === false
        );
    }

    const buttonStyles = {
        enabled: {
            backgroundColor: '#8E51FF',
            fontWeight: 'bold',
            padding: '10px 20px',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
        },
        disabled: {
            backgroundColor: '#6B7280',
            fontWeight: 'bold',
            padding: '10px 20px',
            color: '#9CA3AF',
            border: 'none',
            borderRadius: '6px',
            cursor: 'not-allowed'
        }
    };

    return (
        <div className='container-xxl my-5 z-0'>
            <div className='mb-5'>
                <h1>Cadastrar novo projeto</h1>
                <Subtitle>Defina as informações do seu projeto para obter soluções personalizadas.</Subtitle>
                
                <div className='mt-3'>
                    <button
                        type='button'
                        onClick={handleQuickFill}
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #8E51FF',
                            color: '#8E51FF',
                            fontWeight: 'bold',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                        }}
                    >
                        Preencher com dados de exemplo
                    </button>
                </div>
            </div>

            <Accordion title="Dados do cliente">
                <div className='row' >
                    <div className='col-4'>
                        <CompanyComboBox
                            label="Empresa"
                            placeholder="Selecione a empresa"
                            mandatory={true}
                            items={companies}
                            onSelect={updateCompany}
                            selectedCompanyId={projectDataStore.customerData.company?.id}
                            onCompanyCreated={onCompanyCreated}
                        />
                    </div>

                    <div className='col-8'>
                        <Input
                            label="Nome do ponto focal"
                            placeholder="Digite o nome e sobrenome"
                            mandatory={false}
                            value={projectDataStore.customerData.focalPoint}
                            onChange={(e) => updateFocalPoint(e.target.value)}
                        />
                    </div>

                    <div className='col-8'>
                        <Input
                            label="Cargo e área do ponto focal"
                            placeholder="Digite o cargo e a área (Ex: Gerente de TI)"
                            mandatory={false}
                            value={projectDataStore.customerData.focalPointPosition}
                            onChange={(e) => updateFocalPointPosition(e.target.value)}
                        />
                    </div>

                    <div className='col-4'>
                        <Input
                            label="E-mail do ponto focal"
                            placeholder="Digite o e-mail"
                            mandatory={false}
                            value={projectDataStore.customerData.focalPointEmail}
                            onChange={(e) => updateFocalPointEmail(e.target.value)}
                        />
                    </div>

                    <div className='col-12'>
                        <TextArea 
                            label="Descritivo do negócio"
                            placeholder="Descreva o negócio do cliente e principais produtos"
                            mandatory={true}
                            tooltipText="Exemplo: Grande empresa de energia com atuação nacional que possui 2 principais produtos, Geração Distribuida Compartilhada (GDC) e Mercado Livre"
                            style={{ minHeight: '300px' }}
                            value={projectDataStore.customerData.businessDescritive}
                            onChange={(e) => updateBusinessDescritive(e.target.value)}
                        />
                    </div>
                </div>
            </Accordion>

            <Accordion title="Detalhes do projeto">
                <div className='row'>
                    <div className='col-12'>
                        <Input
                            label="Título do projeto"
                            placeholder="Digite o título do projeto"
                            mandatory={true}
                            value={projectDataStore.projectData.title}
                            onChange={(e) => updateProjectTitle(e.target.value)}
                        />
                    </div>

                    <div className='col-8'>
                        <Dropdown
                            label="Tipo de projeto"
                            items={projectTypesPT}
                            onSelect={updateProjectType}
                            mandatory={true}
                        />
                    </div>

                    <div className='col-2'>
                        <DatePicker 
                            label='Início do projeto' 
                            mandatory={true} 
                            value={projectDataStore.projectData.startDate}
                            onChange={updateStartDate}
                        />
                    </div>

                    <div className='col-2'>
                        <DatePicker 
                            label='Fim do projeto' 
                            mandatory={false} 
                            value={projectDataStore.projectData.endDate}
                            onChange={updateEndDate}
                        />
                    </div>

                    <div className='col-12'>
                        <TextArea 
                            label="Objetivo"
                            placeholder="Descreva o objetivo do projeto."
                            mandatory={true}
                            tooltipText="Por exemplo: Conduzir pesquisa de mercado para identificar oportunidades e estratégias que aumentem a conversão e retenção de clientes PJ e PF no mercado de energia. "
                            style={{ minHeight: '300px' }}
                            value={projectDataStore.projectData.goal}
                            onChange={(e) => updateGoal(e.target.value)}
                        />
                    </div>

                    <div className='col-12'>
                        <TextArea 
                            label="Contexto do negócio"
                            placeholder="Descreva o contexto do negócio, qual o cenário da empresa que originou o desafio do projeto"
                            mandatory={false}
                            tooltipText="Por exemplo: Em um cenário de transição e aumento da concorrência no setor de energia, a empresa perdeu market share em seus principais produtos. A ausência de uma cultura centrada no cliente agrava o desafio frente a novos players de perfil varejista. Portanto, o desafio é aprofundar o conhecimento sobre os clientes para desenvolver estratégias de canais e produtos mais eficazes, viabilizando a expansão da empresa neste novo mercado."
                            style={{ minHeight: '300px' }}
                            value={projectDataStore.projectData.businessContext}
                            onChange={(e) => updateBusinessContext(e.target.value)}
                        />
                    </div>

                    <div className='col-12'>
                        <TextArea 
                            label="Público alvo"
                            placeholder="Descreva o público alvo do projeto"
                            mandatory={true}
                            tooltipText="Exemplo: Empresas que já migraram para o Mercado Livre de energia, Consumidores de conteúdo de reality shows, Time de TI da empresa, etc."
                            style={{ minHeight: '300px' }}
                            value={projectDataStore.projectData.targetPublic}
                            onChange={(e) => updateTargetPublic(e.target.value)}
                        />
                    </div>
                </div>
            </Accordion>

            <Accordion title="Impacto">
                <div className='row'>
                    <div className='col-12'>
                        <TextArea 
                            label="Resultados esperados"
                            placeholder="Descreva o que a empresa espera como resultado do projeto"
                            mandatory={false}
                            tooltipText="Exemplo: Expandir presença e reconhecimento de marca, Reduzir o Custo de Aquisição de Clientes (CAC), Redução do churn atual."
                            style={{ minHeight: '300px' }}
                            value={projectDataStore.impact.expectedResults}
                            onChange={(e) => updateExpectedResults(e.target.value)}
                        />
                    </div>

                    <div className='col-12'>
                        <InputTags
                            label="Indicadores de negócio impactados"
                            placeholder="Digite e pressione 'Enter' para adicionar novas tags."
                            tooltipText="Exemplo: Custo de Aquisição (CAC), Churn Rate, NPS, Market Share, etc."
                            value={projectDataStore.impact.impactedBusinessIndicators}
                            onChange={updateImpactedBusinessIndicators}
                        />
                    </div>

                    <div className='col-12'>
                        <InputTags
                            label="Entregáveis"
                            placeholder="Digite e pressione 'Enter' para adicionar novas tags"
                            mandatory={false}
                            tooltipText="Exemplo: Jornada AS IS, Perfis, Benchmark, Direcionamentos estratégicos, Cardápio de ideias, etc."
                            value={projectDataStore.impact.deliverables}
                            onChange={updateDeliverables}
                        />
                    </div>
                </div>
            </Accordion>

            <div className='w-auto d-flex flex-row justify-content-end gap-3'>
                <button
                    className='btn'
                    onClick={handleCancel}
                    style={{ 
                        backgroundColor: 'transparent', 
                        border: '1px solid #314158',
                        color: '#fff',
                        padding: '10px 20px',
                        borderRadius: '6px'
                    }}
                >
                    Cancelar
                </button>

                <button
                    className='btn'
                    onClick={handleSaveAndContinue}
                    style={isFormDisabled() ? buttonStyles.disabled : buttonStyles.enabled}
                    disabled={isFormDisabled()}
                >
                    Salvar e continuar
                </button>
            </div>
        </div>
    )
}

export default NewProject;